import { useEffect, useState } from "react";
import { History, Loader2, Clock, GitCompareArrows } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { VIEWS } from "@/components/admin/InsightViews";

const fmt = (iso) => {
  if (!iso) return "Unknown date";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

export function InsightHistory({ kind, label, current, open, onOpenChange }) {
  const [versions, setVersions] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [compare, setCompare] = useState(false);

  useEffect(() => {
    if (!open || !kind) return;
    setVersions(null);
    setSelectedId(null);
    api.get("/admin/marketing/history", { params: { kind } })
      .then(({ data }) => { setVersions(data); if (data[0]) setSelectedId(data[0].id); })
      .catch(() => setVersions([]));
  }, [open, kind]);

  if (!kind) return null;
  const View = VIEWS[kind];
  const selected = (versions || []).find((v) => v.id === selectedId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col" data-testid="insight-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-navy flex items-center gap-2">
            <History className="w-5 h-5 text-brand-red" /> {label} — insight history
          </DialogTitle>
          <DialogDescription className="text-brand-ink2">
            Each time you regenerate, the previous version is archived here (last 10 kept). Pick a version to review or compare it with the current one.
          </DialogDescription>
        </DialogHeader>
        {versions === null ? (
          <div className="h-40 grid place-items-center"><Loader2 className="w-6 h-6 text-brand-red animate-spin" /></div>
        ) : versions.length === 0 ? (
          <div className="h-40 grid place-items-center text-center text-brand-ink2 text-sm px-4" data-testid="insight-history-empty">
            No past versions yet. The next time you regenerate these insights, the current version will be archived here.
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-4 min-h-0 flex-1">
            <div className="md:w-56 shrink-0 space-y-1.5 md:overflow-y-auto" data-testid="insight-history-versions">
              {versions.map((v, i) => (
                <button
                  key={v.id}
                  onClick={() => setSelectedId(v.id)}
                  data-testid={`history-version-${i}`}
                  className={`btn-focus w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${v.id === selectedId ? "border-brand-navy bg-brand-bg" : "border-brand-line hover:bg-brand-bg"}`}
                >
                  <p className="text-sm font-bold text-brand-navy">Version {versions.length - i}</p>
                  <p className="text-xs text-brand-ink2 flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(v.generated_at)}</p>
                </button>
              ))}
              <label className="flex items-center justify-between gap-2 rounded-xl border border-brand-line px-3 py-2.5 mt-2 cursor-pointer">
                <span className="text-sm font-semibold text-brand-navy flex items-center gap-1.5">
                  <GitCompareArrows className="w-4 h-4 text-brand-red" /> Compare with current
                </span>
                <Switch checked={compare} onCheckedChange={setCompare} data-testid="history-compare-toggle" />
              </label>
            </div>
            <div className="flex-1 min-w-0 overflow-y-auto pr-1">
              {compare ? (
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-brand-line p-3" data-testid="history-archived-pane">
                    <p className="text-xs font-bold uppercase tracking-wide text-brand-ink2 mb-2">Archived · {fmt(selected?.generated_at)}</p>
                    {selected && <View d={selected.data} />}
                  </div>
                  <div className="rounded-xl border-2 border-brand-navy p-3" data-testid="history-current-pane">
                    <p className="text-xs font-bold uppercase tracking-wide text-brand-navy mb-2">Current · {fmt(current?.generated_at)}</p>
                    {current?.data ? <View d={current.data} /> : <p className="text-sm text-brand-ink2">No current version.</p>}
                  </div>
                </div>
              ) : (
                selected && <View d={selected.data} />
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
